import {
  assembleManifest,
  ingestCapture,
  sha256Hex,
  webDeviceId,
  CaptureError,
  SEGMENT_BYTES,
  type CaptureMeta,
  type CaptureProgress,
  type CaptureSegment,
  type IngestReceipt,
  type Provenance,
} from './capture.ts';

/**
 * File upload → evidence ingest.
 *
 * The sibling of `lib/capture.ts`: where that records bytes from the camera,
 * this takes a file a custodian already holds (a recording, a photo, a document
 * exported from a body-worn camera) and ingests it through the *same* contract.
 * The bytes are chunked and hashed **in the browser**, the RFC 6962 Merkle root
 * is computed over the segment hashes, and the recording goes into the evidence
 * service over the exact ingest session/parts/commit sequence a device meets
 * (evac-evidence spec 02-evidence-core §1.1–1.2, §1.5).
 *
 * **The honesty decision is identical, and load-bearing.** A file uploaded
 * through a browser is a **third-party upload**, graded `declared` — never
 * `captured`. The service reserves `captured` for a device that signs its
 * manifest with a key in a secure element (spec §1.1); a browser has none, so
 * the manifest is `captured_by: 'third_party'`, `signatures: []`, and the grade
 * is `declared`. What such an asset can honestly assert is "integrity
 * guaranteed from ingest at T, provenance as declared by X" and never more
 * (spec §1.5). The uploader states that provenance; the platform records it
 * verbatim, hashes the bytes itself, and countersigns — the chain begins the
 * moment the platform first saw the bytes, not at a trusted sensor.
 *
 * This reuses `lib/capture.ts`'s primitives (`assembleManifest`, the token
 * exchange, the session/parts/commit orchestrator) rather than duplicating the
 * ingest wire contract — the only thing that differs is where the bytes come
 * from and that the provenance is user-declared rather than boilerplate.
 */

/** The provenance the uploading user declares (spec §1.5). */
export interface FileProvenance extends Provenance {
  /**
   * When the uploader says the file was originally captured. Optional — many
   * sources cannot say. It is a *declaration* recorded in provenance, never an
   * attestation, so it lives here and not in the manifest's capture window
   * (which honestly records only when this browser handled the bytes).
   */
  claimed_captured_at?: string;
}

export interface FileProvenanceFields {
  sourceDescription: string;
  originalCustodian: string;
  howObtained: string;
  declaredBy: string;
  /** A local datetime string (`<input type="datetime-local">`); converted to ISO with offset. */
  claimedCapturedAt?: string;
}

/**
 * Build the provenance block from the form, trimming and dropping empties.
 * `claimed_captured_at` is normalised to a full ISO-8601 instant with offset —
 * the shape the service's schema requires (`z.string().datetime({ offset })`).
 */
export function buildFileProvenance(fields: FileProvenanceFields): FileProvenance {
  const provenance: FileProvenance = {
    source_description: fields.sourceDescription.trim(),
    original_custodian: fields.originalCustodian.trim(),
    how_obtained: fields.howObtained.trim(),
    declared_by: fields.declaredBy.trim(),
  };
  const claimed = fields.claimedCapturedAt?.trim();
  if (claimed) {
    const at = new Date(claimed);
    if (!Number.isNaN(at.getTime())) provenance.claimed_captured_at = at.toISOString();
  }
  return provenance;
}

/** Every provenance field the schema requires is present and non-empty. */
export function provenanceComplete(fields: FileProvenanceFields): boolean {
  return (
    fields.sourceDescription.trim().length > 0 &&
    fields.originalCustodian.trim().length > 0 &&
    fields.howObtained.trim().length > 0 &&
    fields.declaredBy.trim().length > 0
  );
}

/**
 * Chunk a file into segments, hashing each in the browser. The asset hash is
 * the Merkle root over these (computed in `assembleManifest`), so a partially
 * transferred file stays verifiable segment-by-segment.
 *
 * An empty file is refused: the manifest schema requires a positive byte count
 * and at least one segment, so a zero-byte file is not an asset.
 */
export async function fileToSegments(
  file: File,
  threshold = SEGMENT_BYTES,
): Promise<CaptureSegment[]> {
  if (file.size === 0) {
    throw new CaptureError('opening', 'an empty file has nothing to ingest as evidence');
  }
  const segments: CaptureSegment[] = [];
  let index = 0;
  for (let offset = 0; offset < file.size; offset += threshold) {
    const bytes = new Uint8Array(await file.slice(offset, offset + threshold).arrayBuffer());
    segments.push({ index, bytes, sha256: await sha256Hex(bytes) });
    index += 1;
  }
  return segments;
}

/** The progress phases the upload UI renders: a hashing pass, then the ingest hops. */
export type FileIngestProgress = { phase: 'hashing' } | CaptureProgress;

/**
 * Ingest one file end to end: hash it into segments, build the declared
 * third-party manifest, then run the shared token-exchange → open session →
 * PUT every part → complete → receipt orchestrator (`ingestCapture`).
 *
 * `threshold` is exposed for tests (to force multiple segments from a small
 * file) and mirrors the device contract's configurable segment size; callers
 * leave it at the default.
 */
export async function ingestFile(
  orgId: string,
  file: File,
  provenance: FileProvenance,
  onProgress: (progress: FileIngestProgress) => void,
  threshold = SEGMENT_BYTES,
): Promise<IngestReceipt> {
  onProgress({ phase: 'hashing' });
  const segments = await fileToSegments(file, threshold);

  const handledAt = new Date().toISOString();
  const meta: CaptureMeta = {
    mediaType: file.type || 'application/octet-stream',
    // The manifest's capture window records only when this browser handled the
    // bytes — an honest claim. Any *claimed* original capture time is a
    // declaration and lives in the provenance block, not here.
    startedAt: handledAt,
    endedAt: handledAt,
    deviceId: webDeviceId(),
    custodian: provenance.declared_by,
  };
  const manifest = await assembleManifest(segments, meta);

  return ingestCapture(orgId, { manifest, provenance, segments }, onProgress);
}
